import { Dispatch, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { difference, isEmpty } from 'lodash';
import { PublicCompanyAdjustmentStatus, SecurityModel } from 'api';
import { BACKSOLVE, OPM, SCENARIO_METHODS_BACKSOLVE, WATERFALL } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import {
  MARKET_ADJUSTMENT_REQUIREMENT,
  VALUATIONS_MARKET_ADJUSTMENT_ADD,
  VALUATIONS_MARKET_ADJUSTMENT_REMOVE,
  VALUATIONS_SLUG,
} from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { CapTable } from 'common/types/allocation';
import { CapTableInfo } from 'common/types/capTable';
import { Format } from 'common/types/format';
import { MeasurementDate } from 'common/types/measurementDate';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomCompactContainer, CustomContainer } from 'components';
import GridButtonWithTooltip from 'components/ActionButtons/GridButtonWithTooltip';
import { LayoutContext } from 'context';
import BacksolveContext, { BacksolveContextTypes } from 'context/BacksolveContext';
import { useExportSignal } from 'features/pdfExport';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getValidSecurities } from 'pages/CapTable/cap-table/utilities';
import BacksolveSummaryTable from 'pages/Valuations/approaches/backsolveApproach/BacksolveSummaryTable/BacksolveSummaryTable';
import BacksolveTable from 'pages/Valuations/approaches/backsolveApproach/BacksolveTable/BacksolveTable';
import {
  ALLOCATION_METHOD_ALIAS,
  BACKSOLVE_TABLE_NAMES,
  CAP_TABLE_ALIAS,
  VALUATION_APPROACH_GPC_ALIAS,
} from 'pages/Valuations/approaches/backsolveApproach/constants';
import MarketAdjustmentLedger from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/MarketAdjustmentLedger';
import { AS_OF_DATE_ALIAS } from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/constants';
import MarketAdjustmentTable from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentTable/MarketAdjustmentTable';
import OPMInputTable from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/OPMInputTable';
import SecurityTable from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/SecurityTable';
import { TARGET_VALUE_ALIAS } from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/util/constants';
import getRowConfig from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/util/getRowConfig';
import useCalculateEquityValue from 'pages/Valuations/approaches/backsolveApproach/useCalculateEquityValue';
import { BACKSOLVE_APPROACH, PdfExportTargets, PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import { useApproachRegistry } from 'pages/ValuationsAllocation/context/ApproachRegistryContext';
import { Note } from 'pages/ValuationsAllocation/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import CapatableService from 'services/captable';
import { useNotes } from 'services/hooks/notes';
import AddActionButton from './AddActionButton/AddActionButton';
import getBacksolveTableRowConfig from './BacksolveTable/util/rowconfig';
import calculateWeightedPerShareValues from './calculateWeightedPerShareValue';
import { useMarketAdjustmentTableChange } from './hooks/useMarketAdjustmentTableChange';
import {
  BackSolveColumn,
  BacksolveProps,
  BackSolveSpreadsheets,
  LedgerData,
  SecuritiesResponse,
  ValuationApproachBackSolveExtended,
} from './types';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const {
  BACKSOLVE: BACKSOLVE_TABLE,
  BACKSOLVE_SUMMARY,
  MARKET_ADJUSTMENT,
  OPM_INPUTS,
  SECURITIES,
} = BACKSOLVE_TABLE_NAMES;

const VALUATION_BACKSOLVE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${BACKSOLVE_TABLE}-container`;
const VALUATION_BACKSOLVE_SECURITES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${SECURITIES}-container`;
const VALUATION_BACKSOLVE_OPM_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${OPM_INPUTS}-container`;
const VALUATION_BACKSOLVE_SUMMARY_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${BACKSOLVE_SUMMARY}-container`;
const VALUATION_BACKSOLVE_MARKET_ADJUSTMENT_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${MARKET_ADJUSTMENT}-container`;

export const getOpmData = (spreadsheets: BackSolveSpreadsheets): ICell[] => {
  const opmCells = spreadsheets.opmInputSheet.cells;
  // Maturity, volatility, risk-free rate, and backsolve date
  return [opmCells!.B2, opmCells!.B5, opmCells!.B6, opmCells!.B1];
};

export const getEditableSecurityValues = (securities: { security: string; shares: string }[]) =>
  securities.map(({ security, shares }) => ({ security, shares }));

const Backsolve = ({
  spreadsheets,
  onChange: onWorkbookChange,
  workbook,
  approaches,
  capTableList,
  approach,
}: BacksolveProps) => {
  const {
    resetConfiguration,
    marketDate,
    measurementDate,
    setNotesInApproach,
    updateApproachesScenarioMethods,
    backsolveAttributes,
    publicCompsAttrs,
  } = useContext(ValuationContext);

  const [marketAdjustment, setMarketAdjustment] = useState<any>();

  const { viewOnlyUser, isExportMode } = useContext(LayoutContext);

  const { notes, setNotes, notesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const approachPanelId = approach.panelId;
  const { isDisabled } = spreadsheets.backsolveSheet.tableData as unknown as { isDisabled: boolean };

  // Approach Registry: register this backsolve if it depends on a PC
  const { stableIds, dependencyGraph } = useApproachRegistry();

  const { register: registerNode, unregister: unregisterNode } = dependencyGraph;
  const { getOrCreateId } = stableIds;

  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE }) as [Format, Dispatch<unknown>];

  const [calculateEquityValue, isLoading] = useCalculateEquityValue();

  const hasGpcApproach = useMemo(
    () => approaches.filter(({ approach_type }) => approach_type === PUBLIC_COMPANIES).length,
    [approaches]
  );

  const backsolveStableId = useMemo(() => getOrCreateId(approachPanelId), [getOrCreateId, approachPanelId]);

  const getSelectedCapTable = useCallback(
    (capTableId: string | number) => {
      if (Array.isArray(capTableList)) {
        return capTableList?.find(({ id }) => id?.toString() === capTableId?.toString());
      }
    },
    [capTableList]
  );

  const getAsOfDate = useCallback(
    () =>
      // if as_of_date is not set it will be an empty string, so we use the || check to default to the marketDate
      spreadsheets.backsolveSheet.tableData.approach.valuations_approach_backsolve.as_of_date || marketDate,
    [spreadsheets, marketDate]
  );

  const getAppliedMethodologies = useCallback(() => {
    const [, ...tmpApppliedMethodologies] = spreadsheets.backsolveSheet.columns;
    return tmpApppliedMethodologies;
  }, [spreadsheets]);

  const checkIfIsOpm = useCallback(() => {
    const methods = getAppliedMethodologies();

    return methods.some((x: any) => Number(x.allocation_method) === Number(OPM));
  }, [getAppliedMethodologies]);

  // flag to hide and display the opm inputs table
  const [isOpm, setOpm] = useState(checkIfIsOpm());

  const getApproachByPanelId = useCallback(
    (id: string | number) => {
      if (!id) {
        return undefined;
      }
      return approaches
        .filter(({ approach_type }) => approach_type === PUBLIC_COMPANIES)
        .find(
          approachCandidate =>
            approachCandidate.panelId === id ||
            (approachCandidate.valuations_approach_gpc?.id &&
              approachCandidate.valuations_approach_gpc.id?.toString() === id.toString())
        );
    },
    [approaches]
  );

  const [gpcApproach, setGpcApproach] = useState(
    getApproachByPanelId(approach.valuations_approach_backsolve.gpc_approach ?? '')
  );

  // this useEffect hooks is meant to update the default gpc approach in the case the market date
  // changes. If the component has mounted at the time of the change, we need something
  // to pick up on that and update the gpc approach.
  useEffect(() => {
    const tmpGpcApproach = getApproachByPanelId(approach.valuations_approach_backsolve.gpc_approach ?? '');
    const compared = gpcApproach === tmpGpcApproach;
    if (!compared && marketDate) {
      setGpcApproach(tmpGpcApproach);
    }
  }, [marketDate, approach, getApproachByPanelId, gpcApproach]);

  const [selectedCapTable, setSelectedCapTable] = useState(
    getSelectedCapTable(spreadsheets.backsolveSheet.cells?.capTableSelection?.value as string)
  );

  useEffect(() => {
    if (capTableList?.length > 0) {
      setSelectedCapTable(getSelectedCapTable(spreadsheets.backsolveSheet.cells?.capTableSelection?.value as string));
    }
  }, [capTableList, getSelectedCapTable, spreadsheets]);

  const [prevOpmData, setPrevOpmData] = useState();
  const [prevEditableSecurityValues, setPrevEditableSecurityValues] = useState();
  const [prevTargetValue, setPrevTargetValue] = useState<any>();
  const [securityList, setSecurityList] = useState<SecurityModel[]>();

  const captableChange = useCallback(
    (capTable: CapTable) => {
      const securityCells = spreadsheets.securitySheet.cells;
      Object.values(securityCells).forEach(cell => {
        if (cell.alias === 'security') {
          onWorkbookChange(cell, '');
        }
      });
      const updatedApproach = { ...approach };
      const firstAppliedMethodology = updatedApproach.valuations_approach_backsolve.applied_methodologies[0];
      firstAppliedMethodology.cap_table = capTable.id as number;

      const updatedRowConfig = getBacksolveTableRowConfig({ captableList: capTableList, approach: updatedApproach });
      spreadsheets.backsolveSheet.reset({
        rowConfig: updatedRowConfig,
      });
      resetConfiguration?.();
    },
    [onWorkbookChange, spreadsheets, resetConfiguration, capTableList, approach]
  );

  useEffect(() => {
    const svc = new CapatableService();
    if (selectedCapTable) {
      svc.getCaptableById<SecuritiesResponse>(selectedCapTable.id!).then(response => {
        setSecurityList(response.securities);
      });
    }
  }, [selectedCapTable]);

  const getSecurityRows = useCallback(
    () => approach.valuations_approach_backsolve.securities_basket.basket,
    [approach]
  );

  const getAllocationMethods = useCallback(
    () => approach.valuations_approach_backsolve.applied_methodologies,
    [approach]
  );

  const securityRows = useMemo(() => {
    if (approach) {
      return getSecurityRows();
    }
    return [];
  }, [getSecurityRows, approach]);

  const allocationMethods = useMemo(() => {
    if (approach) {
      return getAllocationMethods();
    }
    return [];
  }, [getAllocationMethods, approach]);

  const getScenarioMethod = useCallback(
    () => spreadsheets.backsolveSheet.cells?.allocationMethod?.value,
    [spreadsheets]
  );

  const allocation = useMemo(() => approach.allocationId, [approach.allocationId]);

  const areAppliedMethodologiesValid = useCallback(() => {
    const methodologies = (spreadsheets.backsolveSheet.tableData as any).approach?.valuations_approach_backsolve
      ?.applied_methodologies;

    const duplicatedAllocationMethods = methodologies.filter(
      (m: any) => methodologies.filter((x: any) => x.allocation_method === m.allocation_method)?.length > 1
    );

    return duplicatedAllocationMethods.length === 0;
  }, [spreadsheets]);

  const arePresentShareValuesReady = useMemo(() => {
    const methodologies = (spreadsheets.backsolveSheet.tableData as any).approach?.valuations_approach_backsolve
      ?.applied_methodologies;
    if (methodologies) {
      return methodologies.every((m: any) => m.present_share_values.length > 0);
    }
    return false;
  }, [spreadsheets]);

  const getTargetValue = useCallback(() => {
    const rowIndex = (spreadsheets.securitySheet.tableData as any).securitiesBasket.basket.length + 2;
    const cellKey = `D${rowIndex}`;
    return (spreadsheets.securitySheet.cells as any)[cellKey]?.value;
  }, [spreadsheets]);

  useEffect(() => {
    if (selectedCapTable && areAppliedMethodologiesValid() && !isEmpty(securityList) && !arePresentShareValuesReady) {
      const data = {
        captable: selectedCapTable,
        scenarioType: BACKSOLVE,
        allocation,
        appliedMethodologies: getAppliedMethodologies(),
        opmData: getOpmData(spreadsheets),
        securityRows,
        scenarioMethod: getScenarioMethod(),
        targetValue: getTargetValue(),
        prevOpmData,
        editableLedgerCells: getEditableSecurityValues(securityRows),
        prevEditableLedgerCells: prevEditableSecurityValues,
        prevTargetValue,
        securityList,
        spreadsheets,
        onCellsChanged: onWorkbookChange,
      };

      calculateEquityValue(data);
    }
    // disabling because we only want this to load when the page does, or when the security list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityList, arePresentShareValuesReady]);

  const recalculateEquityValue = useCallback(
    async (captable: CapTable, basket = securityRows, targetValue = null) => {
      if (captable && areAppliedMethodologiesValid()) {
        const data = {
          captable: captable.id,
          scenarioType: BACKSOLVE,
          allocation,
          appliedMethodologies: getAppliedMethodologies(),
          marketDate: getAsOfDate(),
          opmData: getOpmData(spreadsheets),
          securityRows: basket,
          scenarioMethod: getScenarioMethod(),
          targetValue: targetValue ?? getTargetValue(),
          prevOpmData,
          editableLedgerCells: getEditableSecurityValues(basket),
          prevEditableLedgerCells: prevEditableSecurityValues,
          prevTargetValue,
          securityList,
          spreadsheets,
          onCellsChanged: onWorkbookChange,
        };
        const calculatedEquityValue = await calculateEquityValue(data);
        return calculatedEquityValue || {};
      }
    },
    [
      areAppliedMethodologiesValid,
      allocation,
      getScenarioMethod,
      getAppliedMethodologies,
      getAsOfDate,
      onWorkbookChange,
      spreadsheets,
      getTargetValue,
      calculateEquityValue,
      securityList,
      securityRows,
      prevTargetValue,
      prevOpmData,
      prevEditableSecurityValues,
    ]
  );

  // Use custom hook for market adjustment table changes
  const { onMarketAdjustmentTableChange, isLoadingMarketAdjustment } = useMarketAdjustmentTableChange({
    gpcApproach,
    approach,
    setMarketAdjustment,
    backsolveAttributes: backsolveAttributes || undefined,
    publicCompsAttributes: publicCompsAttrs || undefined,
  });

  const onChange = useCallback(
    (cell: any, value: any, skipEquityRecalculation = false) => {
      // Handle market adjustment table changes
      if (cell.alias === AS_OF_DATE_ALIAS) {
        onMarketAdjustmentTableChange(value);
      }

      // changes from market adjustment table should not have the equity value recalculated
      if (cell.alias === VALUATION_APPROACH_GPC_ALIAS && value) {
        // should probably be a panel id
        setGpcApproach(getApproachByPanelId(value));
      }
      if (cell.alias === TARGET_VALUE_ALIAS) {
        setPrevTargetValue(cell.value);
        // calculate pershare values, total values, and equity values
      }
      let capTable = selectedCapTable;
      if (cell.alias === CAP_TABLE_ALIAS) {
        capTable = getSelectedCapTable(value);
        if (capTable && capTable.id !== selectedCapTable?.id) {
          setSelectedCapTable(capTable);
          captableChange(capTable);
        }
      }
      onWorkbookChange(cell, value);
      if (cell.alias === ALLOCATION_METHOD_ALIAS) {
        setOpm(checkIfIsOpm());
        updateApproachesScenarioMethods();
      }
      // backsolve
      if (!skipEquityRecalculation) {
        recalculateEquityValue(capTable as CapTable);
      }
    },
    [
      onMarketAdjustmentTableChange,
      selectedCapTable,
      onWorkbookChange,
      checkIfIsOpm,
      getApproachByPanelId,
      getSelectedCapTable,
      captableChange,
      recalculateEquityValue,
      updateApproachesScenarioMethods,
    ]
  );

  const [hasMarketAdjustment, setHasMarketAdjustment] = useState(
    approach.valuations_approach_backsolve.adjust_for_market
  );

  useEffect(() => {
    // Register if market adjustment is enabled and PC approach is set
    if (hasMarketAdjustment && gpcApproach && backsolveStableId) {
      const sourceOfTruthStableId = getOrCreateId(gpcApproach.panelId!);
      registerNode({
        approachId: backsolveStableId,
        approachType: BACKSOLVE_APPROACH,
        panelId: approachPanelId,
        sourceOfTruthType: PUBLIC_COMPANIES,
        sourceOfTruthStableId,
      });
    } else {
      // Unregister if market adjustment is disabled
      unregisterNode(backsolveStableId);
    }
  }, [
    approachPanelId,
    hasMarketAdjustment,
    gpcApproach,
    backsolveStableId,
    registerNode,
    unregisterNode,
    getOrCreateId,
  ]);

  useEffect(() => {
    if (approach.valuations_approach_backsolve.market_adjustment) {
      setMarketAdjustment(approach.valuations_approach_backsolve.market_adjustment);
    }
  }, [approach]);

  const changeMarketAdjustment = useCallback(() => {
    const { valuations_approach_backsolve } = approach;
    // eslint-disable-next-line max-len
    valuations_approach_backsolve.adjust_for_market = !valuations_approach_backsolve.adjust_for_market;
    if (!valuations_approach_backsolve.adjust_for_market) {
      // eslint-disable-next-line no-param-reassign
      valuations_approach_backsolve.gpc_approach = null;
      (valuations_approach_backsolve.public_companies_status ?? []).forEach((status: PublicCompanyAdjustmentStatus) => {
        // eslint-disable-next-line no-param-reassign
        status.is_deleted = true;
      });
      setMarketAdjustment(approach.valuations_approach_backsolve.market_adjustment);
    }
    setHasMarketAdjustment(valuations_approach_backsolve.adjust_for_market);
  }, [approach]);

  // this is used to set and reset the enterprise value in the market adjustment table
  // as that cell is used to update the panel and the summary. When we remove a market adjustment
  // this erases the market adjustment cell and thus makes the enterprise value in both tables
  // equal.
  useEffect(() => {
    let value = 0;
    if (hasMarketAdjustment) {
      value = Number(marketAdjustment);
    }
    // only fire if spreadsheets.marketAdjustmentSheet.cells.MarketAdjustment is different from the value
    const cellValue = Number(spreadsheets.marketAdjustmentSheet.cells?.MarketAdjustment.value);

    if (cellValue !== value) {
      onChange(spreadsheets.marketAdjustmentSheet.cells?.MarketAdjustment as ICell, value);
    }
  }, [hasMarketAdjustment, marketAdjustment, onChange, spreadsheets.marketAdjustmentSheet.cells?.MarketAdjustment]);

  const updateMethodologies = (currentAllocationMethods: BackSolveColumn[]) =>
    currentAllocationMethods.forEach(item => {
      // eslint-disable-next-line no-param-reassign
      item.weight = currentAllocationMethods.length === 1 ? '1' : item.weight;
      // eslint-disable-next-line no-param-reassign
      item.isDeleteableColumn = currentAllocationMethods.length > 1;
    });

  const addAllocationMethod = useCallback(async () => {
    const defaultCapTable = capTableList?.length ? capTableList.find(ct => ct.is_primary)?.id : undefined;

    const { backsolveSheet } = spreadsheets;
    const { applied_methodologies: currentAllocationMethods } = (backsolveSheet.tableData as any).approach
      .valuations_approach_backsolve;
    currentAllocationMethods.push({
      allocation_method: WATERFALL.toString(),
      cap_table: selectedCapTable?.id ?? defaultCapTable,
      weight: '',
      maturity: null,
      volatility: null,
      present_share_values: [],
    });

    updateMethodologies(currentAllocationMethods);

    backsolveSheet.reset({
      tableData: backsolveSheet.tableData,
      columns: [[], ...currentAllocationMethods],
    });
    resetConfiguration?.();
    updateApproachesScenarioMethods();
  }, [resetConfiguration, spreadsheets, capTableList, selectedCapTable, updateApproachesScenarioMethods]);

  const addSecurity = useCallback(() => {
    const { securitySheet } = spreadsheets;
    const { basket } = (securitySheet.tableData as any).securitiesBasket;
    basket.push({
      security: '',
      shares: '',
      value: '',
      per_share: '',
    });

    securitySheet.reset({
      tableData: securitySheet.tableData,
      rowConfig: getRowConfig(basket),
    });
    resetConfiguration?.();
  }, [resetConfiguration, spreadsheets]);

  const deleteSecurity = useCallback(
    async (securityIndex: number) => {
      const { securitySheet } = spreadsheets;
      const { basket, target_value: tv } = (securitySheet.tableData as any).securitiesBasket;
      basket.splice(securityIndex - 1, 1);

      // if the basket is empty, reset the sheet and add a new row to have a clean state
      if (isEmpty(basket)) {
        securitySheet.reset({
          tableData: securitySheet.tableData,
          rowConfig: getRowConfig(basket),
        });
        addSecurity();
        resetConfiguration?.();
        return;
      }
      // if not, recalculate equity value based on new basket but same target value
      // get distribution for new equity value
      const { newDistribution } = (await recalculateEquityValue(selectedCapTable as CapTable, basket, tv)) as any;
      const methods = getAppliedMethodologies();
      const newPresentValues = newDistribution?.map((dist: any) => dist.presentValues);
      const totalPresentShareValues = calculateWeightedPerShareValues(newPresentValues, methods);

      const basketToSet = basket.map((row: any) => {
        const perShare = totalPresentShareValues?.find(
          ({ securityId }: any) => Number(securityId) === Number(row.security)
        );
        return { ...row, per_share: perShare?.value };
      });
      securitySheet.reset({
        tableData: securitySheet.tableData,
        rowConfig: getRowConfig(basketToSet),
      });

      resetConfiguration?.();
    },
    [spreadsheets, recalculateEquityValue, selectedCapTable, getAppliedMethodologies, resetConfiguration, addSecurity]
  );

  const deleteAllocationMethod = useCallback(
    async (allocationMethodIndex: number) => {
      const { backsolveSheet } = spreadsheets;

      const { applied_methodologies: currentAllocationMethods } = (backsolveSheet.tableData as any).approach
        .valuations_approach_backsolve;
      // if it was stored I want to store the deleted id in "deleted_methodologies"
      if (currentAllocationMethods.length > 1) {
        const deletedMethod = currentAllocationMethods.splice(allocationMethodIndex - 1, 1);
        const deletedMethodId = deletedMethod[0].id;

        if (deletedMethodId) {
          (backsolveSheet.tableData as any).approach.valuations_approach_backsolve.deleted_methodologies.push(
            deletedMethodId
          );
        }

        updateMethodologies(currentAllocationMethods);

        backsolveSheet.reset({
          tableData: backsolveSheet.tableData,
          columns: [[], ...currentAllocationMethods],
        });
        resetConfiguration?.();
        setOpm(checkIfIsOpm());
        // Recalculate Equity Value when there's only 1 method left
        if (currentAllocationMethods.length === 1) {
          recalculateEquityValue(selectedCapTable as CapTable);
        }
      }
    },
    [checkIfIsOpm, resetConfiguration, spreadsheets, recalculateEquityValue, selectedCapTable]
  );

  // When a change is made to a ledger we need to save the values returned from that save.
  const onMarketAdjustmentLedgerChange = useCallback(
    (ledgerData: LedgerData) => {
      const { marketAdjustmentSheet } = spreadsheets;
      const changes: [ICell, string | number][] = [];
      changes.push(
        [marketAdjustmentSheet.cells!.AsOfDate, ledgerData.AsOfDate],
        [marketAdjustmentSheet.cells!.Metric, ledgerData.Metric],
        [marketAdjustmentSheet.cells!.MarketAdjustment, ledgerData.AppliedAdjustment]
      );
      changes.forEach(([cell, expr]) => {
        onChange(cell, expr, true);
      });
      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_backsolve.public_companies_status = ledgerData.compData.map((comp: any) => ({
        id: comp.id,
        public_company: comp.public_company ?? null,
        ltm_revenue: comp.ltm_revenue,
        ltm_ebitda: comp.ltm_ebitda,
        ntm_revenue: comp.ntm_revenue,
        ntm_ebitda: comp.ntm_ebitda,
        market_cap: comp.market_cap,
        enterprise_value: comp.enterprise_value,
        enabled: comp.enabled,
        reference_for_backsolve: comp.reference_for_backsolve,
        ticker_symbol: comp.identifier,
        cap_iq_id: comp.cap_iq_id,
        valuation_approach_backsolve: approach.valuations_approach_backsolve.id ?? null,
      }));
      const { savedPublicCompStatusIds } = marketAdjustmentSheet.tableData as any;
      const toPersistPublicCompStatusIds = Array.from(new Set(ledgerData.compData.map(comp => comp.id)));
      const toDeletePublicCompStatusIds = Array.from(
        difference(savedPublicCompStatusIds, toPersistPublicCompStatusIds)
      );
      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_backsolve.deleted_public_company_status_ids = Array.from(
        new Set([
          ...(approach.valuations_approach_backsolve.deleted_public_company_status_ids ?? []),
          ...toDeletePublicCompStatusIds,
        ])
      );

      // also we store some of the other data from the ledger
      const otherUpdates = {
        percentile_selection_a: ledgerData.percentile_selection_a_selection,
        percentile_selection_b: ledgerData.percentile_selection_b_selection,
        adjustment_selected: ledgerData.SelectedAdjustment,
        specified_adjustment: ledgerData.SpecifiedAdjustment,
      };
      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_backsolve = {
        ...approach.valuations_approach_backsolve,
        ...otherUpdates,
      } as unknown as ValuationApproachBackSolveExtended;
      setMarketAdjustment(ledgerData.AppliedAdjustment);
    },
    [approach, onChange, spreadsheets]
  );

  const backsolveContextData = useMemo(
    () =>
      ({
        gpcApproach,
        securityList,
        spreadsheets,
        approach,
        onMarketAdjustmentLedgerChange,
        measurementDate,
        format,
        formatDispatch,
      }) as unknown as BacksolveContextTypes,
    [
      gpcApproach,
      securityList,
      spreadsheets,
      approach,
      onMarketAdjustmentLedgerChange,
      measurementDate,
      format,
      formatDispatch,
    ]
  );

  useEffect(() => {
    if (!isEmpty(notes)) {
      setNotesInApproach?.((prevState: any) => {
        const tmpNotes = prevState.filter((note: Note) => note.panelId !== approachPanelId);
        return [...tmpNotes, { notes, panelId: approachPanelId, notesHasChanged }];
      });
    }
  }, [notes, notesHasChanged, approachPanelId, setNotesInApproach]);

  let backsolveExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading || isLoadingMarketAdjustment || isEmpty(spreadsheets)) {
    backsolveExportStatus = 'loading';
  } else if (approach?.valuations_approach_backsolve?.securities_basket?.basket === undefined) {
    backsolveExportStatus = 'loading';
  } else if (isEmpty(securityRows)) {
    backsolveExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.BACKSOLVE.id,
    status: backsolveExportStatus,
  });

  return (
    <>
      <CustomContainer data-ref={PdfExportTargets.BACKSOLVE.id} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        <BacksolveContext.Provider value={backsolveContextData}>
          {/* Backsolve */}
          <CustomCompactContainer
            className={clsx(VALUATION_BACKSOLVE_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}>
            {/* Backsolve */}
            <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <BacksolveTable
                deleteColFn={deleteAllocationMethod}
                isLoading={isLoading}
                onChange={onChange}
                spreadsheets={spreadsheets}
                workbook={workbook}
              />
            </Grid>

            {/* Add allocation method */}
            {!isExportMode && (
              <Grid size={{ xs: 12 }}>
                <AddActionButton
                  onClickHandler={addAllocationMethod}
                  text="Add Allocation Method"
                  disabled={allocationMethods.length >= SCENARIO_METHODS_BACKSOLVE.length || isDisabled}
                />
              </Grid>
            )}
          </CustomCompactContainer>

          {/* Securities */}
          <CustomCompactContainer
            className={clsx(VALUATION_BACKSOLVE_SECURITES_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}>
            {/* Securities */}
            <Grid size={{ xs: 12 }}>
              <SecurityTable
                spreadsheets={spreadsheets}
                onChange={onChange}
                setPrevEditableSecurityValues={setPrevEditableSecurityValues}
                securities={securityRows}
                securityList={securityList}
                deleteRowFn={deleteSecurity}
                workbook={workbook}
              />
            </Grid>

            {/* Add row */}
            {!isExportMode && (
              <Grid size={{ xs: 12 }}>
                <AddActionButton
                  onClickHandler={addSecurity}
                  disabled={
                    securityRows.length >= getValidSecurities(selectedCapTable as unknown as CapTableInfo).length ||
                  isDisabled
                  }
                  text="Add Row"
                />
              </Grid>
            )}
          </CustomCompactContainer>

          {/* OPM */}
          {isOpm && (
            <Grid
              className={clsx(VALUATION_BACKSOLVE_OPM_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
              size={{ xs: 12 }}>
              <OPMInputTable
                componentId="approach-opm-input-table"
                spreadsheets={spreadsheets}
                onChange={onChange}
                setPrevOpmData={setPrevOpmData}
                fullWidth={false}
                workbook={workbook}
                measurementDate={measurementDate as MeasurementDate}
              />
            </Grid>
          )}

          {/* Backsolve summary */}
          <Grid
            className={clsx(VALUATION_BACKSOLVE_SUMMARY_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
            size={{ xs: 12 }}>
            <BacksolveSummaryTable
              spreadsheets={spreadsheets}
              onChange={onChange}
              format={format}
              formatDispatch={formatDispatch}
              workbook={workbook}
            />
          </Grid>

          {/* Market adjustment — hidden from main sheet in export mode when it has its own sheet */}
          {!(isExportMode && hasMarketAdjustment) && (
            <CustomCompactContainer
              className={clsx(VALUATION_BACKSOLVE_MARKET_ADJUSTMENT_CONTAINER_CLASS, {
                'is-export-mode': isExportMode,
              })}>
              {!viewOnlyUser && !isExportMode && (
                <Grid size={{ xs: 12 }}>
                  <GridButtonWithTooltip
                    buttonText={
                      hasMarketAdjustment ? VALUATIONS_MARKET_ADJUSTMENT_REMOVE : VALUATIONS_MARKET_ADJUSTMENT_ADD
                    }
                    className="btn-table-aligned"
                    disabled={!hasGpcApproach || !approach.id}
                    icon={hasMarketAdjustment ? <RemoveIcon /> : <AddIcon />}
                    onClick={changeMarketAdjustment}
                    tooltipText={!hasGpcApproach || !approach.id ? MARKET_ADJUSTMENT_REQUIREMENT : ''}
                  />
                </Grid>
              )}

              {/* Market adjustment */}
              <Grid size={{ xs: 12 }}>
                {hasMarketAdjustment && (
                  <MarketAdjustmentTable
                    spreadsheets={spreadsheets}
                    onChange={onChange}
                    format={format}
                    formatDispatch={formatDispatch}
                    workbook={workbook}
                    isLoadingMarketAdjustment={isLoadingMarketAdjustment}
                  />
                )}
              </Grid>
            </CustomCompactContainer>
          )}

          <WidgetDrawer
            applicationContext={WidgetDrawerApplicationContext.BACKSOLVE}
            notesProps={{
              pageType: VALUATIONS_PAGE_VALUE,
              pageTypeKey: VALUATIONS_PAGE_KEY,
              pageTypeId: approach.id,
              notes,
              isApproach: true,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            }}
          />
        </BacksolveContext.Provider>
      </CustomContainer>

      {/* Market Adjustment Ledger sheet — rendered as a separate capture target in export mode */}
      {isExportMode && hasMarketAdjustment && (
        <CustomContainer
          data-ref={PdfExportTargets.BACKSOLVE_MARKET_ADJUSTMENT_LEDGER.id}
          style={EXPORT_MODE_STYLES}>
          <BacksolveContext.Provider value={backsolveContextData}>
            <Grid
              className={clsx(VALUATION_BACKSOLVE_MARKET_ADJUSTMENT_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
              size={{ xs: 12 }}>
              <MarketAdjustmentTable
                spreadsheets={spreadsheets}
                onChange={onChange}
                format={format}
                formatDispatch={formatDispatch}
                workbook={workbook}
                isLoadingMarketAdjustment={isLoadingMarketAdjustment}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MarketAdjustmentLedger />
            </Grid>
          </BacksolveContext.Provider>
        </CustomContainer>
      )}
    </>
  );
};

export default Backsolve;
